import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { Add, AddShoppingCart, Inventory2, Remove, Search, ShoppingCart, Send, CardGiftcard } from '@mui/icons-material'
import { apiService } from '../../services/api'
import { WARZONE_COIN_IMAGE_URL } from '../../properties'
import { useAuth } from '../../contexts/AuthContext'
import { usePalworldPerfil } from '../../contexts/PalworldPerfilContext'
import type { PalworldItem, PalworldItemsPageData } from '../../types/palworldItems'
import { usePalworldCart } from '../../contexts/PalworldCartContext'
import PalworldCartDrawer from '../../components/palworld/cart/PalworldCartDrawer'
import PalworldGrantDrawer from '../../components/palworld/grant/PalworldGrantDrawer'
import PalworldKitInicialDrawer, { type PalworldKitInicialLine } from '../../components/palworld/kit-inicial/PalworldKitInicialDrawer'

const extractItems = (raw: any): PalworldItemsPageData | null => {
  const data = raw?.data ?? raw
  if (!data || !Array.isArray(data?.content)) return null
  return data as PalworldItemsPageData
}

const formatPrice = (value: number | undefined) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '—'
  return value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const PriceWithCoin: React.FC<{
  value: number | undefined
  align?: 'left' | 'right'
  fullWidth?: boolean
}> = ({
  value,
  align = 'left',
  fullWidth = false
}) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      justifyContent: align === 'right' ? 'flex-end' : 'flex-start',
      width: fullWidth ? '100%' : 'auto'
    }}
  >
    <Typography variant="body2">{formatPrice(value)}</Typography>
    <Box
      component="img"
      src={WARZONE_COIN_IMAGE_URL}
      alt="Warzone Coin"
      sx={{ width: 16, height: 16, opacity: 0.95 }}
      loading="lazy"
    />
  </Box>
)

const PalworldItemsPage: React.FC = () => {
  const { user } = useAuth()
  const { perfil, isLoading: isPerfilLoading } = usePalworldPerfil()
  const { state: cartState, toggleCart, addItem, getItemQuantity } = usePalworldCart()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<PalworldItem[]>([])
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState<PalworldItem | null>(null)
  const [updatingActive, setUpdatingActive] = useState<Record<string, boolean>>({})
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [editForm, setEditForm] = useState<{
    name: string
    assetName: string
    category: string
    description: string
    price: string
    rank: string
    rarity: string
    stackSize: string
    typeA: string
    typeB: string
    weight: string
    active: boolean
    imageBucket: string
    imageKey: string
  } | null>(null)

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(20)
  const [sort, setSort] = useState<'price' | 'name' | 'rarity' | 'rank'>('price')

  const [nameInput, setNameInput] = useState('')
  const [categoryInput, setCategoryInput] = useState('')
  const [rankInput, setRankInput] = useState<string>('')
  const [rarityInput, setRarityInput] = useState<string>('')
  const [activeInput, setActiveInput] = useState<string>('')

  const [categories, setCategories] = useState<string[]>([])
  const [categoriesLoading, setCategoriesLoading] = useState(false)
  const [categoriesError, setCategoriesError] = useState<string | null>(null)

  const [nameQuery, setNameQuery] = useState<string | undefined>(undefined)
  const [categoryQuery, setCategoryQuery] = useState<string | undefined>(undefined)
  const [rankQuery, setRankQuery] = useState<number | undefined>(undefined)
  const [rarityQuery, setRarityQuery] = useState<number | undefined>(undefined)
  const [activeQuery, setActiveQuery] = useState<boolean | undefined>(undefined)

  const isAdmin = useMemo(() => {
    const roles = (user?.roles ?? []).map((r) => String(r).toLowerCase())
    return roles.some((r) => ['admin', 'administrator'].includes(r))
  }, [user?.roles])

  const vipDiasRestantes = Number(perfil?.vipAtual?.diasRestantes ?? 0) || 0
  const isVip = vipDiasRestantes > 0

  const [buyQty, setBuyQty] = useState(1)
  const [grantQty, setGrantQty] = useState(1)

  type GrantLine = { id: string; item: PalworldItem; quantity: number }
  const [grantOpen, setGrantOpen] = useState(false)
  const [grantLines, setGrantLines] = useState<GrantLine[]>([])

  const grantTotalItems = useMemo(() => grantLines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0), [grantLines])
  const grantQtyById = useMemo(() => {
    const map: Record<string, number> = {}
    for (const l of grantLines) map[l.id] = l.quantity
    return map
  }, [grantLines])

  // Kit Inicial
  const [kitInicialOpen, setKitInicialOpen] = useState(false)
  const [kitInicialLines, setKitInicialLines] = useState<PalworldKitInicialLine[]>([])
  const [editingKitInicial, setEditingKitInicial] = useState<import('../../types/palworldItems').KitInicial | null>(null)

  const kitInicialTotalItems = useMemo(() => kitInicialLines.reduce((sum, l) => sum + (Number(l.quantity) || 0), 0), [kitInicialLines])
  const kitInicialQtyById = useMemo(() => {
    const map: Record<string, number> = {}
    for (const l of kitInicialLines) map[l.id] = l.quantity
    return map
  }, [kitInicialLines])

  const addToGrantCart = (item: PalworldItem, quantity = 1) => {
    const qty = Math.max(1, Math.trunc(Number(quantity) || 1))
    setGrantLines((prev) => {
      const idx = prev.findIndex((l) => l.id === item.id)
      if (idx >= 0) return prev.map((l) => (l.id === item.id ? { ...l, quantity: l.quantity + qty } : l))
      return [...prev, { id: item.id, item, quantity: qty }]
    })
    setGrantOpen(true)
  }

  const setGrantLineQty = (id: string, quantity: number) => {
    const qty = Math.max(1, Math.trunc(Number(quantity) || 1))
    setGrantLines((prev) => prev.map((l) => (l.id === id ? { ...l, quantity: qty } : l)))
  }

  const removeGrantLine = (id: string) => setGrantLines((prev) => prev.filter((l) => l.id !== id))
  const clearGrantCart = () => setGrantLines([])

  // Kit Inicial functions
  const addToKitInicial = (item: PalworldItem, quantity = 1) => {
    const qty = Math.max(1, Math.trunc(Number(quantity) || 1))
    setKitInicialLines((prev) => {
      const idx = prev.findIndex((l) => l.id === item.id)
      if (idx >= 0) return prev.map((l) => (l.id === item.id ? { ...l, quantity: l.quantity + qty } : l))
      return [...prev, { id: item.id, item, quantity: qty }]
    })
    setKitInicialOpen(true)
  }

  const setKitInicialLineQty = (id: string, quantity: number) => {
    const qty = Math.max(1, Math.trunc(Number(quantity) || 1))
    setKitInicialLines((prev) => prev.map((l) => (l.id === id ? { ...l, quantity: qty } : l)))
  }

  const removeKitInicialLine = (id: string) => setKitInicialLines((prev) => prev.filter((l) => l.id !== id))
  const clearKitInicialCart = () => setKitInicialLines([])

  useEffect(() => {
    setBuyQty(1)
    setGrantQty(1)
  }, [selected?.id])

  useEffect(() => {
    let mounted = true

    const run = async () => {
      setCategoriesLoading(true)
      setCategoriesError(null)
      try {
        const data = await apiService.listarPalworldItemCategories()
        if (!mounted) return
        setCategories(Array.isArray(data) ? data : [])
      } catch (e: any) {
        if (!mounted) return
        setCategories([])
        setCategoriesError(e?.message || 'Erro ao carregar categorias.')
      } finally {
        if (!mounted) return
        setCategoriesLoading(false)
      }
    }

    run()
    return () => {
      mounted = false
    }
  }, [])

  const canBuySelected = useMemo(() => {
    if (isAdmin) return false
    if (!selected) return false
    const price = Number(selected.price ?? NaN)
    return Number.isFinite(price) && price > 0
  }, [isAdmin, selected])

  const selectedInCartQty = useMemo(() => {
    if (!selected) return 0
    return getItemQuantity(selected.id)
  }, [getItemQuantity, selected])

  const addSelectedToCart = () => {
    if (!selected) return
    if (!canBuySelected) {
      setError('Este item não está disponível para compra (preço inválido).')
      return
    }
    addItem(selected, buyQty)
    setSelected(null)
  }

  const query = useMemo(
    () => ({
      page,
      size: rowsPerPage,
      sort,
      name: nameQuery,
      category: categoryQuery,
      rank: rankQuery,
      rarity: rarityQuery,
      active: isAdmin ? activeQuery : undefined
    }),
    [activeQuery, categoryQuery, isAdmin, nameQuery, page, rankQuery, rarityQuery, rowsPerPage, sort]
  )

  useEffect(() => {
    if (!selected) {
      setIsEditing(false)
      setEditForm(null)
      return
    }

    setIsEditing(false)
    setEditForm({
      name: selected.name ?? '',
      assetName: selected.assetName ?? '',
      category: selected.category ?? '',
      description: selected.description ?? '',
      price: selected.price === undefined || selected.price === null ? '' : String(selected.price),
      rank: selected.rank === undefined || selected.rank === null ? '' : String(selected.rank),
      rarity: selected.rarity === undefined || selected.rarity === null ? '' : String(selected.rarity),
      stackSize: selected.stackSize === undefined || selected.stackSize === null ? '' : String(selected.stackSize),
      typeA: selected.typeA ?? '',
      typeB: selected.typeB ?? '',
      weight: selected.weight === undefined || selected.weight === null ? '' : String(selected.weight),
      active: typeof selected.active === 'boolean' ? selected.active : true,
      imageBucket: selected.imageS3?.bucket ?? '',
      imageKey: selected.imageS3?.key ?? ''
    })
  }, [selected])

  const setItemActiveLocal = (id: string, active: boolean | undefined) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, active } : it)))
    setSelected((prev) => (prev?.id === id ? { ...prev, active } : prev))
  }

  const toggleActive = async (item: PalworldItem) => {
    if (!isAdmin) return
    if (typeof item.active !== 'boolean') return
    if (updatingActive[item.id]) return

    const nextActive = !item.active
    const prevActive = item.active

    setError(null)
    setUpdatingActive((prev) => ({ ...prev, [item.id]: true }))
    setItemActiveLocal(item.id, nextActive)

    try {
      await apiService.setPalworldItemActive(item.id, nextActive)
    } catch (e: any) {
      setItemActiveLocal(item.id, prevActive)
      setError(e?.message || 'Erro ao atualizar status do item.')
    } finally {
      setUpdatingActive((prev) => {
        const copy = { ...prev }
        delete copy[item.id]
        return copy
      })
    }
  }

  const saveEdit = async () => {
    if (!isAdmin) return
    if (!selected || !editForm) return
    if (saving) return

    const name = editForm.name.trim()
    const assetName = editForm.assetName.trim()
    const category = editForm.category.trim()

    if (!name || !assetName || !category) {
      setError('Preencha os campos obrigatórios: Nome, Asset e Categoria.')
      return
    }

    const toNumber = (raw: string): number | undefined => {
      const v = raw.trim()
      if (!v) return undefined
      const n = Number(v.replace(',', '.'))
      return Number.isFinite(n) ? n : undefined
    }

    const price = toNumber(editForm.price)
    const rank = toNumber(editForm.rank)
    const rarity = toNumber(editForm.rarity)
    const stackSize = toNumber(editForm.stackSize)
    const weight = toNumber(editForm.weight)

    if (editForm.price.trim() && price === undefined) {
      setError('Preço inválido.')
      return
    }
    if (editForm.rank.trim() && rank === undefined) {
      setError('Rank inválido.')
      return
    }
    if (editForm.rarity.trim() && rarity === undefined) {
      setError('Raridade inválida.')
      return
    }
    if (editForm.stackSize.trim() && stackSize === undefined) {
      setError('Stack inválido.')
      return
    }
    if (editForm.weight.trim() && weight === undefined) {
      setError('Peso inválido.')
      return
    }

    const bucket = editForm.imageBucket.trim()
    const key = editForm.imageKey.trim()
    if ((bucket && !key) || (!bucket && key)) {
      setError('Para ImageS3, preencha Bucket e Key.')
      return
    }

    setSaving(true)
    setError(null)
    try {
      const payload = {
        assetName,
        category,
        description: editForm.description.trim() || undefined,
        name,
        price,
        rank: rank === undefined ? undefined : Math.trunc(rank),
        rarity: rarity === undefined ? undefined : Math.trunc(rarity),
        stackSize: stackSize === undefined ? undefined : Math.trunc(stackSize),
        typeA: editForm.typeA.trim() || undefined,
        typeB: editForm.typeB.trim() || undefined,
        weight,
        active: editForm.active,
        imageS3: bucket && key ? { bucket, key } : undefined
      }

      const raw = await apiService.atualizarPalworldItem(selected.id, payload)
      const updated = (raw?.data ?? raw) as Partial<PalworldItem> | null
      const merged: PalworldItem = {
        ...selected,
        ...payload,
        ...(updated ?? {}),
        id: selected.id
      }

      setItems((prev) => prev.map((it) => (it.id === selected.id ? merged : it)))
      setSelected(merged)
      setIsEditing(false)
    } catch (e: any) {
      setError(e?.message || 'Erro ao editar item.')
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    let mounted = true
    const run = async () => {
      setLoading(true)
      setError(null)
      try {
        const raw = await apiService.listarPalworldItems(query)
        if (!mounted) return
        const data = extractItems(raw)
        setItems(data?.content ?? [])
        setTotal(Number(data?.totalElements ?? 0) || 0)
      } catch (e: any) {
        if (!mounted) return
        setError(e?.message || 'Erro ao consultar itens.')
      } finally {
        if (!mounted) return
        setLoading(false)
      }
    }

    run()
    return () => {
      mounted = false
    }
  }, [query])

  const doSearch = () => {
    const nameTrimmed = nameInput.trim()
    setNameQuery(nameTrimmed ? nameTrimmed : undefined)

    const categoryTrimmed = categoryInput.trim()
    setCategoryQuery(categoryTrimmed ? categoryTrimmed : undefined)

    const rankTrimmed = rankInput.trim()
    setRankQuery(rankTrimmed ? Number(rankTrimmed) : undefined)

    const rarityTrimmed = rarityInput.trim()
    setRarityQuery(rarityTrimmed ? Number(rarityTrimmed) : undefined)

    if (isAdmin) {
      const v = activeInput.trim()
      if (v === 'true') setActiveQuery(true)
      else if (v === 'false') setActiveQuery(false)
      else setActiveQuery(undefined)
    } else {
      setActiveQuery(undefined)
    }

    setPage(0)
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
      <Stack spacing={2.5}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Inventory2 />
              Itens
            </Typography>
            <Typography sx={{ color: 'text.secondary' }}>
              Catálogo de itens do Palworld.
            </Typography>
            {!isAdmin && (
              <Box sx={{ mt: 1 }}>
                <Chip
                  size="small"
                  color={isVip ? 'success' : 'default'}
                  variant="outlined"
                  label={
                    isPerfilLoading && !perfil
                      ? 'VIP: carregando…'
                      : isVip
                        ? `VIP ativo • ${vipDiasRestantes.toLocaleString('pt-BR')}d`
                        : 'Sem VIP'
                  }
                  sx={{ borderColor: 'rgba(255,255,255,0.16)', fontWeight: 800 }}
                />
              </Box>
            )}
          </Box>

          {!isAdmin && (
            <Button
              variant="outlined"
              onClick={toggleCart}
              startIcon={
                <Badge color="success" badgeContent={cartState.totalItems} invisible={cartState.totalItems === 0}>
                  <ShoppingCart />
                </Badge>
              }
              sx={{ borderColor: 'rgba(255,255,255,0.14)' }}
            >
              Carrinho
            </Button>
          )}

          {isAdmin && (
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={() => setKitInicialOpen(true)}
                startIcon={
                  <Badge color="success" badgeContent={kitInicialTotalItems} invisible={kitInicialTotalItems === 0}>
                    <CardGiftcard />
                  </Badge>
                }
                sx={{ fontWeight: 900, background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)', color: '#1a3a1a' }}
              >
                Criar Kit
              </Button>
              <Button
                variant="contained"
                onClick={() => setGrantOpen(true)}
                startIcon={
                  <Badge color="success" badgeContent={grantTotalItems} invisible={grantTotalItems === 0}>
                    <Send />
                  </Badge>
                }
                sx={{ fontWeight: 900 }}
              >
                Enviar itens
              </Button>
            </Stack>
          )}
        </Box>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 3 } }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={isAdmin ? 3 : 4}>
                <TextField
                  fullWidth
                  size="small"
                  label="Nome"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') doSearch()
                  }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small" error={!!categoriesError}>
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={categoryInput}
                    label="Category"
                    onChange={(e) => {
                      setCategoryInput(String(e.target.value))
                      setPage(0)
                    }}
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {categoriesLoading && categories.length === 0 && (
                      <MenuItem value="__loading" disabled>
                        Carregando...
                      </MenuItem>
                    )}
                    {categories.map((cat) => (
                      <MenuItem key={cat} value={cat}>
                        {cat}
                      </MenuItem>
                    ))}
                  </Select>
                  {!!categoriesError && <FormHelperText>{categoriesError}</FormHelperText>}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rank</InputLabel>
                  <Select
                    value={rankInput}
                    label="Rank"
                    onChange={(e) => {
                      setRankInput(String(e.target.value))
                      setPage(0)
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {[...Array(10)].map((_, idx) => (
                      <MenuItem key={idx + 1} value={String(idx + 1)}>
                        {idx + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={1}>
                <FormControl fullWidth size="small">
                  <InputLabel>Rarity</InputLabel>
                  <Select
                    value={rarityInput}
                    label="Rarity"
                    onChange={(e) => {
                      setRarityInput(String(e.target.value))
                      setPage(0)
                    }}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {[...Array(10)].map((_, idx) => (
                      <MenuItem key={idx + 1} value={String(idx + 1)}>
                        {idx + 1}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Ordenar</InputLabel>
                  <Select
                    value={sort}
                    label="Ordenar"
                    onChange={(e) => {
                      setSort(e.target.value as any)
                      setPage(0)
                    }}
                  >
                    <MenuItem value="price">Preço</MenuItem>
                    <MenuItem value="name">Nome</MenuItem>
                    <MenuItem value="rarity">Raridade</MenuItem>
                    <MenuItem value="rank">Rank</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {isAdmin && (
                <Grid item xs={12} md={1}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Ativo</InputLabel>
                    <Select
                      value={activeInput}
                      label="Ativo"
                      onChange={(e) => {
                        setActiveInput(String(e.target.value))
                        setPage(0)
                      }}
                    >
                      <MenuItem value="">Todos</MenuItem>
                      <MenuItem value="true">Ativos</MenuItem>
                      <MenuItem value="false">Inativos</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} md={isAdmin ? 2 : 2}>
                <Button fullWidth variant="contained" startIcon={<Search />} onClick={doSearch}>
                  Buscar
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 3, md: 4 } }}>
            {error && <Alert severity="error">{error}</Alert>}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width={64}>Imagem</TableCell>
                        <TableCell>Item</TableCell>
                        <TableCell>Categoria</TableCell>
                        {isAdmin && <TableCell align="center">Ativo</TableCell>}
                        <TableCell align="right">Preço</TableCell>
                        <TableCell align="right">Raridade</TableCell>
                        <TableCell align="right">Rank</TableCell>
                        {isAdmin && <TableCell align="right" width={92}>Kit</TableCell>}
                        {isAdmin && <TableCell align="right" width={92}>Enviar</TableCell>}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {items.map((it) => (
                        <TableRow
                          key={it.id}
                          hover
                          onClick={() => setSelected(it)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>
                            <Avatar
                              variant="rounded"
                              src={it.imageUrl}
                              alt={it.name ?? it.assetName ?? 'item'}
                              sx={{ width: 44, height: 44, bgcolor: 'rgba(255,255,255,0.08)' }}
                              imgProps={{ loading: 'lazy' }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 800 }}>
                              {it.name ?? '—'}
                            </Typography>
                            {!!it.assetName && (
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {it.assetName}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{it.category ?? '—'}</Typography>
                          </TableCell>
                          {isAdmin && (
                            <TableCell align="center">
                              {typeof it.active === 'boolean' ? (
                                <Box
                                  onClick={(e) => e.stopPropagation()}
                                  sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}
                                >
                                  <Switch
                                    size="small"
                                    checked={it.active}
                                    disabled={!!updatingActive[it.id]}
                                    onChange={() => toggleActive(it)}
                                    inputProps={{ 'aria-label': 'Ativar/desativar item' }}
                                  />
                                  {updatingActive[it.id] ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 800, color: it.active ? 'success.main' : 'error.main' }}
                                    >
                                      {it.active ? 'Ativo' : 'Inativo'}
                                    </Typography>
                                  )}
                                </Box>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                          )}
                          <TableCell align="right">
                            <PriceWithCoin value={it.price} align="right" fullWidth />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{it.rarity ?? '—'}</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">{it.rank ?? '—'}</Typography>
                          </TableCell>
                          {isAdmin && (
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                                {kitInicialQtyById[it.id] > 0 && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    No kit: {kitInicialQtyById[it.id]}
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => addToKitInicial(it, 1)}
                                  sx={{ fontWeight: 800 }}
                                >
                                  +1
                                </Button>
                              </Stack>
                            </TableCell>
                          )}
                          {isAdmin && (
                            <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
                                {grantQtyById[it.id] > 0 && (
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    No envio: {grantQtyById[it.id]}
                                  </Typography>
                                )}
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => addToGrantCart(it, 1)}
                                  sx={{ fontWeight: 800 }}
                                >
                                  +1
                                </Button>
                              </Stack>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}

                      {items.length === 0 && !error && (
                        <TableRow>
                          <TableCell colSpan={isAdmin ? 9 : 6}>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              Nenhum item encontrado.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  component="div"
                  count={total}
                  page={page}
                  onPageChange={(_e, p) => setPage(p)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10))
                    setPage(0)
                  }}
                  rowsPerPageOptions={[10, 20, 50, 100]}
                  labelRowsPerPage="Linhas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                  }
                />
              </>
            )}
          </CardContent>
        </Card>
      </Stack>

      <Dialog
        open={!!selected}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.86)' } }}
        PaperProps={{
          sx: {
            bgcolor: 'rgba(18, 18, 30, 0.98)',
            border: '1px solid rgba(255,255,255,0.10)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <span>{selected?.name ?? 'Item'}</span>
          {isAdmin && selected && !isEditing && (
            <Button variant="outlined" onClick={() => setIsEditing(true)}>
              Editar
            </Button>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Box
                  component="img"
                  src={selected.imageUrl}
                  alt={selected.name ?? selected.assetName ?? 'item'}
                  sx={{
                    width: '100%',
                    maxHeight: 340,
                    objectFit: 'contain',
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.10)',
                    bgcolor: 'rgba(255,255,255,0.04)'
                  }}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                {isEditing && isAdmin && editForm ? (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        label="Nome"
                        value={editForm.name}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, name: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        label="Asset"
                        value={editForm.assetName}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, assetName: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        size="small"
                        label="Categoria"
                        value={editForm.category}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, category: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Preço"
                        value={editForm.price}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, price: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Rank"
                        value={editForm.rank}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, rank: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Raridade"
                        value={editForm.rarity}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, rarity: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Stack"
                        value={editForm.stackSize}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, stackSize: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="TypeA"
                        value={editForm.typeA}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, typeA: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="TypeB"
                        value={editForm.typeB}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, typeB: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Peso"
                        value={editForm.weight}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, weight: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        checked={editForm.active}
                        onChange={(_e, checked) => setEditForm((prev) => (prev ? { ...prev, active: checked } : prev))}
                        inputProps={{ 'aria-label': 'Ativo' }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {editForm.active ? 'Ativo' : 'Inativo'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="ImageS3 Bucket"
                        value={editForm.imageBucket}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, imageBucket: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="ImageS3 Key"
                        value={editForm.imageKey}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, imageKey: e.target.value } : prev))}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Descrição"
                        multiline
                        minRows={3}
                        value={editForm.description}
                        onChange={(e) => setEditForm((prev) => (prev ? { ...prev, description: e.target.value } : prev))}
                      />
                    </Grid>
                  </Grid>
                ) : (
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Asset:</strong> {selected.assetName ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Categoria:</strong> {selected.category ?? '—'}
                      </Typography>
                    </Grid>
                    {isAdmin && (
                      <Grid item xs={12} sm={6}>
                        <Typography variant="body2">
                          <strong>Ativo:</strong>{' '}
                          {typeof selected.active === 'boolean' ? (selected.active ? 'Sim' : 'Não') : '—'}
                        </Typography>
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <strong>Preço:</strong>
                        <PriceWithCoin value={selected.price} />
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Peso:</strong> {selected.weight ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Stack:</strong> {selected.stackSize ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Raridade:</strong> {selected.rarity ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>Rank:</strong> {selected.rank ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>TypeA:</strong> {selected.typeA ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2">
                        <strong>TypeB:</strong> {selected.typeB ?? '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 900, mt: 1 }}>
                        Descrição
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                        {selected.description ?? '—'}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        {isAdmin && selected && isEditing && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditing(false)
              }}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button variant="contained" onClick={saveEdit} disabled={saving}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
          </DialogActions>
        )}

        {!isAdmin && selected && (
          <DialogActions sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Quantidade
              </Typography>
              <IconButton
                size="small"
                onClick={() => setBuyQty((q) => Math.max(1, Math.trunc(q) - 1))}
                sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}
              >
                <Remove fontSize="small" />
              </IconButton>
              <TextField
                size="small"
                value={buyQty}
                onChange={(e) => setBuyQty(Math.max(1, Math.trunc(Number(e.target.value) || 1)))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, style: { textAlign: 'center', width: 80 } }}
              />
              <IconButton
                size="small"
                onClick={() => setBuyQty((q) => Math.max(1, Math.trunc(q) + 1))}
                sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}
              >
                <Add fontSize="small" />
              </IconButton>

              {selectedInCartQty > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                  No carrinho: {selectedInCartQty}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button variant="outlined" onClick={toggleCart} startIcon={<ShoppingCart />}>
                Ver carrinho
              </Button>
              <Button
                variant="contained"
                onClick={addSelectedToCart}
                disabled={!canBuySelected}
                startIcon={<AddShoppingCart />}
              >
                Adicionar
              </Button>
            </Stack>
          </DialogActions>
        )}

        {isAdmin && selected && !isEditing && (
          <DialogActions sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 800 }}>
                Quantidade
              </Typography>
              <IconButton
                size="small"
                onClick={() => setGrantQty((q) => Math.max(1, Math.trunc(q) - 1))}
                sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}
              >
                <Remove fontSize="small" />
              </IconButton>
              <TextField
                size="small"
                value={grantQty}
                onChange={(e) => setGrantQty(Math.max(1, Math.trunc(Number(e.target.value) || 1)))}
                inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1, style: { textAlign: 'center', width: 80 } }}
              />
              <IconButton
                size="small"
                onClick={() => setGrantQty((q) => Math.max(1, Math.trunc(q) + 1))}
                sx={{ bgcolor: 'rgba(255,255,255,0.10)' }}
              >
                <Add fontSize="small" />
              </IconButton>

              {(grantQtyById[selected.id] ?? 0) > 0 && (
                <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>
                  No envio: {grantQtyById[selected.id]}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Button
                variant="contained"
                onClick={() => {
                  addToKitInicial(selected, grantQty)
                  setSelected(null)
                }}
                startIcon={<CardGiftcard />}
                sx={{ fontWeight: 900, background: 'linear-gradient(45deg, #4caf50 30%, #66bb6a 90%)', color: '#1a3a1a' }}
              >
                Adicionar ao Kit
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  addToGrantCart(selected, grantQty)
                  setSelected(null)
                }}
                startIcon={<Send />}
                sx={{ fontWeight: 900 }}
              >
                Adicionar ao envio
              </Button>
            </Stack>
          </DialogActions>
        )}
      </Dialog>

      {!isAdmin && <PalworldCartDrawer />}
      {isAdmin && (
        <>
          <PalworldKitInicialDrawer
            open={kitInicialOpen}
            onClose={() => {
              setKitInicialOpen(false)
              setEditingKitInicial(null)
            }}
            lines={kitInicialLines}
            setLineQuantity={setKitInicialLineQty}
            removeLine={removeKitInicialLine}
            clearCart={clearKitInicialCart}
            kitInicial={editingKitInicial}
          />
          <PalworldGrantDrawer
            open={grantOpen}
            onClose={() => setGrantOpen(false)}
            lines={grantLines}
            setLineQuantity={setGrantLineQty}
            removeLine={removeGrantLine}
            clearCart={clearGrantCart}
          />
        </>
      )}
    </Container>
  )
}

export default PalworldItemsPage
